<script lang="ts">
  /**
   * `/invite?token=…` — where a magic link is spent.
   *
   * WHY THERE IS A BUTTON AND NOT AN AUTOMATIC REDIRECT. The token is single-use, and
   * the page must not spend it just because something loaded the URL. It also gives
   * the recipient the one thing an emailed link otherwise lacks: a look at what they
   * are about to accept — which address it signs them in as, and which communities it
   * adds — before it happens. The preview behind it is a read-only GET for the same
   * reason: mail scanners follow links.
   *
   * No password field anywhere on purpose. Clicking signs them in; a password comes
   * later, from forgot-password, if they ever want one.
   */
  import { navigate } from '../lib/router.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import {
    ApiError,
    claimAccountInvite,
    fetchAccountInvitePreview,
    type AccountInvitePreview,
  } from '../lib/admin';

  const token = new URLSearchParams(window.location.search).get('token') ?? '';

  let preview = $state<AccountInvitePreview | null>(null);
  let loading = $state(true);
  let claiming = $state(false);
  let error = $state<string | null>(null);

  async function load(): Promise<void> {
    loading = true;
    error = null;
    try {
      preview = token ? await fetchAccountInvitePreview(token) : { valid: false };
    } catch {
      // A network failure is not a dead link — say so, and offer a retry.
      error = 'Could not check this link. Check your connection and try again.';
    } finally {
      loading = false;
    }
  }
  void load();

  async function accept(): Promise<void> {
    if (claiming) return;
    claiming = true;
    error = null;
    try {
      await claimAccountInvite(token);
      // Signed in — straight to the panel. `claimAccountInvite` has already stored the
      // session, so the admin shell mounts with a token exactly as it does after login.
      navigate('/admin');
    } catch (err: unknown) {
      claiming = false;
      // 410 is the expected failure: spent, revoked or expired. Re-read the preview so
      // the page stops offering a button that cannot work.
      if (err instanceof ApiError && err.status === 410) {
        preview = { valid: false, reason: 'invalid, expired or already used' };
        return;
      }
      error = err instanceof Error ? err.message : 'Could not accept this invite.';
    }
  }

  const communities = $derived(preview?.communities ?? []);
</script>

<div class="flex min-h-screen flex-col items-center justify-center px-5 py-12">
  <div class="console-glow pointer-events-none absolute inset-x-0 top-0 h-64"></div>

  <div class="relative w-full max-w-md">
    <div class="mb-6 flex items-center gap-2">
      <img src="/icon-128.png" alt="" class="h-8 w-8 rounded-md" />
      <span class="font-display text-lg font-bold tracking-tight">Sideguide</span>
    </div>

    {#if loading}
      <StateBlock state="loading" compact />
    {:else if preview?.valid}
      <div class="rounded-box border hair bg-base-100 p-6">
        <!-- Three shapes of link, and each says what it actually does: a plain
             sign-in link (no communities — what a superadmin sends somebody who has
             no password yet), one that adds communities to an account, and one that
             creates the account outright. -->
        <div class="eyebrow">{communities.length === 0 ? 'Sign in' : "You've been invited"}</div>
        <h1 class="mt-1 font-display text-2xl font-bold tracking-tight">
          {#if communities.length === 0}
            Sideguide
          {:else if communities.length === 1}
            {communities[0]}
          {:else}
            {communities.length} communities
          {/if}
        </h1>
        <p class="mt-2 text-sm text-base-content/72">
          {#if communities.length === 0}
            This link signs you in as
          {:else if preview.new_account}
            This link creates your Sideguide account for
          {:else}
            This link adds
            {communities.length === 1 ? 'this community' : 'these communities'} to
          {/if}
          <span class="redact font-mono text-xs">{preview.email}</span>{communities.length === 0
            ? '.'
            : ' and signs you in.'}
        </p>

        {#if communities.length > 1}
          <ul class="mt-4 space-y-1.5">
            {#each communities as name (name)}
              <li class="flex items-center gap-2 rounded-field border hair px-3 py-2 text-sm">
                <span class="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary"></span>
                <span class="redact">{name}</span>
              </li>
            {/each}
          </ul>
        {/if}

        {#if error}
          <div class="mt-4 rounded-field border border-error/30 bg-error/10 px-3 py-2 text-sm text-error">
            {error}
          </div>
        {/if}

        <button class="btn btn-primary mt-5 w-full gap-2" onclick={accept} disabled={claiming}>
          {#if claiming}<span class="loading loading-spinner loading-xs"></span>{/if}
          Continue as <span class="redact">{preview.email}</span>
        </button>
        <p class="mt-3 text-xs text-base-content/55">
          No password needed. You can set one later from the sign-in page if you want it.
        </p>
      </div>
    {:else if error}
      <div class="rounded-box border hair bg-base-100 p-6">
        <div class="eyebrow text-error/80">Something went wrong</div>
        <p class="mt-2 text-sm text-base-content/72">{error}</p>
        <button class="btn btn-sm mt-4" onclick={load}>Try again</button>
      </div>
    {:else}
      <!-- The dead-link case: used, revoked, expired, or never real. One message for
           all four on purpose — telling a stranger holding a URL which of those it is
           would answer a question they should not get to ask. -->
      <div class="rounded-box border hair bg-base-100 p-6">
        <div class="eyebrow">This link isn't usable</div>
        <p class="mt-2 text-sm text-base-content/72">
          Invite links work once and expire after 7 days. Ask whoever sent it for a fresh one.
        </p>
        <button class="btn btn-sm mt-4" onclick={() => navigate('/admin/login')}>
          Go to sign in
        </button>
      </div>
    {/if}
  </div>
</div>
